import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle, Users, DollarSign, Briefcase, Zap, TrendingUp, Wrench, Palette, Music, Video, Camera, Box, Hammer, Cpu } from "lucide-react";
import { CommunityPost } from "@/components/CommunityPost";
import { AddChallengeModal } from "@/components/AddChallengeModal";
import { useAuth } from "@/hooks/useAuth";

const Index = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showChallengeModal, setShowChallengeModal] = useState(false);

  const handlePostChallenge = () => {
    if (user) {
      setShowChallengeModal(true);
    } else {
      navigate("/auth?reason=post_challenge");
    }
  };
  const stats = [
    { value: "36,000+", label: "Challenges Completed" },
    { value: "$12.8M", label: "Rewards Paid Out" },
    { value: "25,000+", label: "Verified Professionals" },
  ];

  const industries = [
    { icon: Cpu, name: "Tech & Engineering" },
    { icon: Box, name: "3D & Animation" },
    { icon: Hammer, name: "Carpentry & Trades" },
    { icon: Wrench, name: "Welding & Metal" },
    { icon: Music, name: "Music & Sound" },
    { icon: Video, name: "Video & Film" },
    { icon: Palette, name: "Design & Creative" },
    { icon: Camera, name: "Photography" },
  ];

  const challengeTypes = [
    {
      icon: Briefcase,
      title: "Hiring Challenges",
      description: "Get hired by completing real work instead of submitting resumes.",
      count: "8,200 open",
    },
    {
      icon: Zap,
      title: "Cash Bounties",
      description: "Solve problems for money. Best solution wins the prize.",
      count: "4,100 open",
    },
    {
      icon: TrendingUp,
      title: "Commission Deals",
      description: "Close deals and earn commission on verified sales.",
      count: "1,800 open",
    },
  ];

  const recentWins = [
    { name: "Sarah C.", skill: "Automation", reward: "$15,000", timeAgo: "2 hours ago" },
    { name: "Marcus W.", skill: "Carpentry", reward: "$18,000", timeAgo: "4 hours ago" },
    { name: "Alex R.", skill: "3D Rendering", reward: "$4,500", timeAgo: "6 hours ago" },
    { name: "Jordan L.", skill: "Audio Engineering", reward: "$6,000", timeAgo: "8 hours ago" },
  ];

  const communityPosts = [
    { id: "1", author: { name: "Sarah Chen", avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop", title: "Automation Engineer", verified: true }, category: "Tech & Engineering", title: "Built a lead scoring system that increased qualified leads by 340%", description: "Complete breakdown of the n8n + GPT-4 workflow I built for a B2B SaaS.", stats: { likes: 234, comments: 45 }, proofType: "verified" as const, image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop", timeAgo: "2 hours ago" },
    { id: "4", author: { name: "Marcus Webb", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop", title: "Master Carpenter", verified: true }, category: "Carpentry", title: "Completed custom walnut dining table with hand-cut joinery", description: "12-foot live edge walnut table with butterfly keys. Client paid $18,000.", stats: { likes: 456, comments: 89 }, proofType: "verified" as const, image: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&h=400&fit=crop", timeAgo: "4 hours ago" },
    { id: "7", author: { name: "Alex Rivera", avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop", title: "3D Artist", verified: true }, category: "3D & Animation", title: "Created photorealistic product renders for luxury watch brand", description: "Full product visualization package including 360 spins and lifestyle shots.", stats: { likes: 312, comments: 67 }, proofType: "verified" as const, image: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&h=400&fit=crop", timeAgo: "6 hours ago" },
    { id: "12", author: { name: "Jordan Lee", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop", title: "Audio Engineer", verified: true }, category: "Music & Sound", title: "Mixed and mastered debut album that hit 1M streams", description: "Indie rock album with warm analog-inspired sound. Mixed in Pro Tools.", stats: { likes: 289, comments: 52 }, proofType: "verified" as const, image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=600&h=400&fit=crop", timeAgo: "8 hours ago" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="pt-24 pb-20 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-lg md:text-xl font-semibold text-primary mb-4 animate-fade-up tracking-wide">
                Interviews lie. Results don't.
              </p>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 animate-fade-up" style={{ animationDelay: "0.05s" }}>
                Interviews measure confidence.
                <br />
                <span className="text-primary">Comp3t3 measures execution.</span>
              </h1>


              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 animate-fade-up" style={{ animationDelay: "0.2s" }}>
                <Button variant="hero" size="lg" asChild>
                  <Link to="/challenges">
                    Browse Challenges
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </Button>
                <Button variant="outline" size="lg" onClick={handlePostChallenge}>
                  Post a Challenge
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-8 max-w-lg mx-auto animate-fade-up" style={{ animationDelay: "0.3s" }}>
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How It Works - Brief */}
        <section className="py-16 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                How It Works
              </h2>
              <p className="text-muted-foreground">
                Simple, transparent, and fair
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-8">
              {[
                { step: "1", title: "Find", desc: "Browse challenges that match your skills" },
                { step: "2", title: "Submit", desc: "Complete the work and submit your solution" },
                { step: "3", title: "Verify", desc: "Company reviews and verifies results" },
                { step: "4", title: "Earn", desc: "Get paid for winning submissions" },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold mx-auto mb-3">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Button variant="outline" asChild>
                <Link to="/how-it-works">
                  Learn How Comp3t3 Works
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Challenge Types */}
        <section className="py-16 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                Three Ways to Earn
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
              {challengeTypes.map((type) => (
                <Link
                  key={type.title}
                  to="/challenges"
                  className="bg-card border border-border rounded-lg p-6 hover:border-primary/30 hover:shadow-card transition-all"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <type.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{type.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{type.description}</p>
                  <span className="text-sm font-medium text-primary">{type.count}</span>
                </Link>
              ))}
            </div>

            {/* Industries */}
            <div className="text-center">
              <h3 className="text-lg font-semibold text-foreground mb-6">
                Opportunities Across 20+ Industries
              </h3>
              <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
                {industries.map((industry) => (
                  <div
                    key={industry.name}
                    className="flex items-center gap-2 px-4 py-2 bg-secondary rounded-full text-sm font-medium text-muted-foreground"
                  >
                    <industry.icon className="w-4 h-4" />
                    {industry.name}
                  </div>
                ))}
                <Link 
                  to="/challenges" 
                  className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary hover:bg-primary/20 transition-colors"
                >
                  +12 more
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Community Feed */}
        <section className="py-16 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-1">From the Community</h2>
                <p className="text-muted-foreground">See what professionals are building and proving</p>
              </div>
              <Button variant="outline" asChild>
                <Link to="/community">
                  View All
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {communityPosts.map((post) => (
                <Link key={post.id} to="/community">
                  <CommunityPost {...post} />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-primary">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-4">
                Ready to Prove Yourself?
              </h2>
              <p className="text-primary-foreground/80 mb-8">
                Join thousands of professionals earning opportunities through verified work.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button 
                  variant="secondary" 
                  size="lg"
                  className="bg-background text-foreground hover:bg-background/90"
                  asChild
                >
                  <Link to="/challenges">Browse Challenges</Link>
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                  onClick={handlePostChallenge}
                >
                  Post a Challenge
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Add Challenge Modal */}
      {user && (
        <AddChallengeModal
          open={showChallengeModal}
          onOpenChange={setShowChallengeModal}
          userId={user.id}
          onSuccess={() => {
            setShowChallengeModal(false);
            navigate("/profile");
          }}
        />
      )}
    </div>
  );
};

export default Index;
